/**
 * StreetAutocompleteInput — a Street Address input backed by Google Places that,
 * on selection, PARSES the place into street / city / state / zip and hands them
 * back so a form with separate address fields can be auto-filled.
 *
 * Used by the tenant onboarding + quickstart pages (which store street, city,
 * state, zipCode separately).
 *
 * Props:
 *   street        current street value (controlled)
 *   onStreetChange(value)   called on every keystroke (keeps typing working)
 *   onSelect({ street, city, state, zip })  called when a place is picked
 */
import React from 'react';
import PlacesSuggestInput from './PlacesSuggestInput';

function parsePlace(place) {
    const comps = place.address_components || [];
    const get = (type, useShort = false) => {
        const c = comps.find((x) => x.types.includes(type));
        return c ? (useShort ? c.short_name : c.long_name) : '';
    };
    const streetNumber = get('street_number');
    const route = get('route');
    const street = [streetNumber, route].filter(Boolean).join(' ').trim()
        // fall back to the leading part of the formatted address if components are sparse
        || (place.formatted_address || '').split(',')[0].trim();
    const city = get('locality') || get('sublocality') || get('postal_town');
    const state = get('administrative_area_level_1', true);
    const zip = get('postal_code');
    return { street, city, state, zip };
}

export default function StreetAutocompleteInput({
    street, onStreetChange, onSelect, placeholder = '123 Main St', size = 'md', ...rest
}) {
    return (
        <PlacesSuggestInput
            value={street}
            onChange={onStreetChange}
            onSelect={({ formattedAddress, addressComponents }) => {
                const parsed = parsePlace({ formatted_address: formattedAddress, address_components: addressComponents });
                if (typeof onSelect === 'function') onSelect(parsed);
            }}
            placeholder={placeholder}
            size={size}
            {...rest}
        />
    );
}
